import React, { useState } from 'react';
import { Form, Input, Button, Card, message, Space, Progress } from 'antd';
import { LockOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { authAPI } from '../api/auth';

const Settings = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  // 计算密码强度
  const calculatePasswordStrength = (password) => {
    if (!password) return 0;
    
    let strength = 0;
    
    // 长度检查
    if (password.length >= 8) strength += 25;
    if (password.length >= 12) strength += 10;
    
    // 包含小写字母
    if (/[a-z]/.test(password)) strength += 20;
    
    // 包含大写字母
    if (/[A-Z]/.test(password)) strength += 20;
    
    // 包含数字
    if (/[0-9]/.test(password)) strength += 20;
    
    // 包含特殊字符
    if (/[^a-zA-Z0-9]/.test(password)) strength += 15;
    
    return Math.min(strength, 100);
  };

  // 获取密码强度颜色和文本
  const getPasswordStrengthStatus = (strength) => {
    if (strength < 40) return { color: '#ff4d4f', text: '弱' };
    if (strength < 70) return { color: '#faad14', text: '中' };
    return { color: '#52c41a', text: '强' };
  };

  // 密码验证规则
  const passwordValidator = (_, value) => {
    if (!value) {
      return Promise.reject(new Error('请输入新密码'));
    }
    
    if (value.length < 8) {
      return Promise.reject(new Error('密码长度至少8位'));
    }
    
    const hasLetter = /[a-zA-Z]/.test(value);
    const hasNumber = /[0-9]/.test(value);
    const hasSpecial = /[^a-zA-Z0-9]/.test(value);
    
    if (!hasLetter || !hasNumber || !hasSpecial) {
      return Promise.reject(new Error('密码必须包含字母、数字和特殊字符'));
    }
    
    return Promise.resolve();
  };

  // 处理新密码输入变化
  const handleNewPasswordChange = (e) => {
    const value = e.target.value;
    const strength = calculatePasswordStrength(value);
    setPasswordStrength(strength);
  };

  // 提交表单
  const handleSubmit = async (values) => {
    setLoading(true);
    
    try {
      const response = await authAPI.changePassword({
        oldPassword: values.oldPassword,
        newPassword: values.newPassword
      });
      
      if (response.success) {
        message.success('密码修改成功，请重新登录');
        form.resetFields();
        setPasswordStrength(0);
        
        // 3秒后跳转到登录页
        setTimeout(() => {
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_info');
          window.location.href = '/login';
        }, 2000);
      } else {
        message.error(response.message || '修改密码失败');
      }
    } catch (error) {
      console.error('修改密码失败:', error);
      message.error(error.response?.data?.message || '修改密码失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const strengthStatus = getPasswordStrengthStatus(passwordStrength);

  return (
    <div style={{ maxWidth: 600, margin: '0 auto' }}>
      <Card 
        title={
          <Space>
            <LockOutlined />
            <span>修改密码</span>
          </Space>
        }
        bordered={false}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
        >
          <Form.Item
            label="原密码"
            name="oldPassword"
            rules={[
              { required: true, message: '请输入原密码' }
            ]}
          >
            <Input.Password 
              size="large"
              placeholder="请输入原密码"
              prefix={<LockOutlined />}
            />
          </Form.Item>

          <Form.Item
            label="新密码"
            name="newPassword"
            rules={[
              { validator: passwordValidator }
            ]}
            extra={
              <div style={{ marginTop: 8 }}>
                <div style={{ marginBottom: 4 }}>
                  密码强度：
                  <span style={{ color: strengthStatus.color, fontWeight: 'bold', marginLeft: 8 }}>
                    {passwordStrength > 0 ? strengthStatus.text : '-'}
                  </span>
                </div>
                <Progress 
                  percent={passwordStrength} 
                  strokeColor={strengthStatus.color}
                  showInfo={false}
                  size="small"
                />
                <div style={{ color: '#8c8c8c', fontSize: 12, marginTop: 8 }}>
                  • 长度至少8位<br/>
                  • 必须包含字母、数字和特殊字符（如 @#$%^&*）
                </div>
              </div>
            }
          >
            <Input.Password 
              size="large"
              placeholder="请输入新密码"
              prefix={<LockOutlined />}
              onChange={handleNewPasswordChange}
            />
          </Form.Item>

          <Form.Item
            label="确认新密码"
            name="confirmPassword"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: '请确认新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password 
              size="large"
              placeholder="请再次输入新密码"
              prefix={<CheckCircleOutlined />}
            />
          </Form.Item>

          <Form.Item style={{ marginTop: 32 }}>
            <Space style={{ width: '100%', justifyContent: 'center' }}>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loading}
                size="large"
                style={{ minWidth: 120 }}
              >
                确认修改
              </Button>
              <Button 
                size="large"
                style={{ minWidth: 120 }}
                onClick={() => {
                  form.resetFields();
                  setPasswordStrength(0);
                }}
              >
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default Settings;
